import { getAlfredApiUrl } from "src/utils";
import {
  AlfredAllConfigs,
  AlfredFiatAccount,
  AlfredFiatAccountRequirements,
  AlfredPaymentMethod,
  AlfredQuote,
  AlfredSupportedOptionsResponse,
  AlfredSupportedPair,
  Bank,
  CreateFiatAccountPayload,
  CreatePaymentMethodPayload,
  GetBanksParams,
  InitiateDepositPayload,
  InitiateDepositResponse,
  InitiateWithdrawalPayload,
  InitiateWithdrawalResponse,
  QuotePreviewPayload,
} from "./types";

export enum AlfredEndpoints {
  CONFIGS = "/psp/alfred/configs",
  SUPPORTED_OPTIONS = "/psp/alfred/supported-options",
  QUOTE_PREVIEW = "/psp/alfred/quote-preview",
  INITIATE_DEPOSIT = "/psp/alfred/initiate-deposit",
  INITIATE_WITHDRAWAL = "/psp/alfred/initiate-withdrawal",
  PAYMENT_METHODS = "/psp/alfred/payment-methods",
  FIAT_ACCOUNTS = "/psp/alfred/fiat-accounts",
  FIAT_ACCOUNT_REQUIREMENTS = "/psp/alfred/fiat-accounts/requirements",
  SESSION = "/psp/alfred/session",
  DEV_EMIT_ONRAMP = "/psp/alfred/__dev/emit-onramp",
  DEV_EMIT_OFFRAMP = "/psp/alfred/__dev/emit-offramp",
  DEV_STATUSES = "/psp/alfred/__dev/statuses",
}

/**
 * Find a supported pair by from/to currency. Use this on the form to
 * derive the min/max amount the user is allowed to type. Returns null
 * when the pair isn't configured at Alfred — the form should show a
 * "pair not supported" state.
 */
export function findPair(
  configs: AlfredAllConfigs,
  fromCurrency: string,
  toCurrency: string,
): AlfredSupportedPair | null {
  const matches = configs.supportedPairs.filter(
    (p) =>
      p.fromCurrency.toUpperCase() === fromCurrency.toUpperCase() &&
      p.toCurrency.toUpperCase() === toCurrency.toUpperCase(),
  );
  if (matches.length === 0) return null;
  return matches.find((p) => p.businessId === null) ?? matches[0];
}

/** Format a customer's Alfred payment method for the bank-picker UI. */
function paymentMethodToBank(pm: AlfredPaymentMethod): Bank {
  const last4 = pm.accountNumber?.slice(-4) ?? "";
  return {
    bankGroupId: pm.paymentMethodId,
    bankGroupName: last4 ? `${pm.bankName} — ****${last4}` : pm.bankName,
    universalBranchCode: pm.countryCode,
  };
}

class AlfredClient {
  private apiUrl: string;
  private authorization = "";

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  /**
   * Forwarded by `useTokenInitializer` once the host app's JWT has
   * been retrieved. Required for endpoints that filter by the user
   * (e.g. `supported-options` narrows fiats to the user's country).
   */
  public setToken(token?: string): void {
    if (token) this.authorization = token;
  }

  /**
   * Talks to the vakotrade backend, which proxies to the Alfred REST
   * API server-side. The widget never holds Alfred's api-secret.
   */
  private async request<T = any>(
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    endpoint: string,
    options?: {
      body?: Record<string, any>;
      params?: Record<string, string>;
    }
  ): Promise<T> {
    try {
      const queryString = options?.params ? "?" + new URLSearchParams(options.params).toString() : "";

      const response = await fetch(`${this.apiUrl.replace("/graphql", "")}${endpoint}${queryString}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...(this.authorization ? { Authorization: this.authorization } : {}),
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.warn(`Request failed: ${response.status} ${response.statusText}. ${errorBody}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to execute REST request:", error);
      throw error;
    }
  }

  /**
   * Fetch Alfred's supported pairs / chains / fiats. Cached server-side
   * (30 min TTL) so this is cheap to call on widget mount.
   */
  public async getConfigs(): Promise<AlfredAllConfigs> {
    return this.request<AlfredAllConfigs>("GET", AlfredEndpoints.CONFIGS);
  }

  /**
   * List every fiat the user can pick for this route in the given
   * direction (`deposit` → onramp, `withdrawal` → offramp). Each option
   * carries its own rail + min/max so the form can validate inline
   * without re-querying when the user changes the picker value.
   */
  public async getSupportedOptions(params: {
    paymentRouteId: string;
    type: "deposit" | "withdrawal";
  }): Promise<AlfredSupportedOptionsResponse> {
    return this.request<AlfredSupportedOptionsResponse>(
      "GET",
      AlfredEndpoints.SUPPORTED_OPTIONS,
      {
        params: {
          payment_route_id: params.paymentRouteId,
          type: params.type,
        },
      }
    );
  }

  /**
   * Live preview-quote: shows the user the stable-equivalent + Alfred
   * fee breakdown while they're typing. NOT bound to a session — a
   * fresh quote is requested at /onramp time on submit.
   */
  public async getQuotePreview(payload: QuotePreviewPayload): Promise<AlfredQuote> {
    return this.request<AlfredQuote>("POST", AlfredEndpoints.QUOTE_PREVIEW, {
      body: payload,
    });
  }

  /**
   * Start an Alfred onramp (deposit) and get back a redirect URL the
   * user should be sent to.
   */
  public async initiateDeposit(payload: InitiateDepositPayload): Promise<InitiateDepositResponse> {
    return this.request<InitiateDepositResponse>("POST", AlfredEndpoints.INITIATE_DEPOSIT, { body: payload });
  }

  /**
   * Submit an Alfred offramp (withdrawal) for an existing payment.
   */
  public async initiateWithdrawal(payload: InitiateWithdrawalPayload): Promise<InitiateWithdrawalResponse> {
    return this.request<InitiateWithdrawalResponse>("POST", AlfredEndpoints.INITIATE_WITHDRAWAL, { body: payload });
  }

  /**
   * Read-only snapshot of the cached widget session — used to poll
   * for transaction status updates while the user is in the SPEI/PIX
   * waiting screen. Returns `null` when the session has expired so
   * the UI can show a "session expired" state instead of looping.
   */
  public async getSession(sessionId: string) {
    return this.request<import("./types").AlfredSessionSnapshot | null>("GET", AlfredEndpoints.SESSION, {
      params: { session_id: sessionId },
    });
  }

  /**
   * Dev/QA-only — runs a preset scenario (`success` / `failure`) for
   * the current session. Backend dispatches the canonical chain
   * in-process with pauses so polling sees state progression. Server
   * enforces the env guard (VAKOTEST + *UAT only) so calling this
   * outside dev just 4xxs.
   */
  public async devEmitOnramp(payload: { session_id: string; scenario: "success" | "failure" }) {
    return this.request<{ session_id: string; transactionId: string; scenario: string }>(
      "POST",
      AlfredEndpoints.DEV_EMIT_ONRAMP,
      { body: payload }
    );
  }

  /**
   * Dev/QA-only counterpart to {@link devEmitOnramp} — walks the
   * canonical OFFRAMP webhook chain. `initiateWithdrawal` already
   * auto-runs the success chain on dev exchanges, so this is mainly
   * useful for re-running or testing the FAILED path from the QA
   * panel.
   */
  public async devEmitOfframp(payload: { session_id: string; scenario: "success" | "failure" }) {
    return this.request<{ session_id: string; transactionId: string; scenario: string }>(
      "POST",
      AlfredEndpoints.DEV_EMIT_OFFRAMP,
      { body: payload }
    );
  }

  /** Discovery list for the QA panel — KYC / ONRAMP / OFFRAMP statuses. */
  public async getDevStatuses() {
    return this.request<import("./types").AlfredDevStatusesResponse>("GET", AlfredEndpoints.DEV_STATUSES);
  }

  /**
   * List the customer's Alfred Payment Methods (registered bank
   * accounts) for the destination picker step.
   */
  public async listPaymentMethods(sessionId: string): Promise<AlfredPaymentMethod[]> {
    return this.request<AlfredPaymentMethod[]>("GET", AlfredEndpoints.PAYMENT_METHODS, {
      params: { session_id: sessionId },
    });
  }

  /**
   * Register a new bank account at Alfred for the customer behind a
   * widget session. Returns the created method (typically `pending`
   * until Alfred verifies it).
   */
  public async createPaymentMethod(payload: CreatePaymentMethodPayload): Promise<AlfredPaymentMethod> {
    return this.request<AlfredPaymentMethod>("POST", AlfredEndpoints.PAYMENT_METHODS, {
      body: payload,
    });
  }

  /**
   * Remove a previously registered bank account.
   */
  public async deletePaymentMethod(sessionId: string, paymentMethodId: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>("DELETE", `${AlfredEndpoints.PAYMENT_METHODS}/${paymentMethodId}`, {
      params: { session_id: sessionId },
    });
  }

  /**
   * Backwards-compat adapter: returns the customer's payment methods
   * mapped onto the legacy `Bank` shape that the existing withdrawal
   * step components consume. New code should call
   * {@link listPaymentMethods} instead.
   *
   * @param params.exchange — kept for signature compatibility, unused
   *   server-side once the session is resolved.
   */
  public async getBanksList(params: GetBanksParams & { session_id?: string }): Promise<Bank[]> {
    if (!params.session_id) return [];
    const methods = await this.listPaymentMethods(params.session_id);
    return methods.map(paymentMethodToBank);
  }

  /**
   * Beneficiary FiatAccount controller — Alfred's newer offramp-recipient
   * registration flow alongside `payment-methods`. Per-rail required
   * fields are exposed via {@link getFiatAccountRequirements} so the
   * "Add bank account" form can render dynamically without a widget
   * redeploy when Alfred onboards a new rail.
   */
  public async listFiatAccounts(sessionId: string): Promise<AlfredFiatAccount[]> {
    return this.request<AlfredFiatAccount[]>("GET", AlfredEndpoints.FIAT_ACCOUNTS, {
      params: { session_id: sessionId },
    });
  }

  public async createFiatAccount(payload: CreateFiatAccountPayload): Promise<AlfredFiatAccount> {
    return this.request<AlfredFiatAccount>("POST", AlfredEndpoints.FIAT_ACCOUNTS, {
      body: payload,
    });
  }

  public async getFiatAccount(sessionId: string, fiatAccountId: string): Promise<AlfredFiatAccount> {
    return this.request<AlfredFiatAccount>("GET", `${AlfredEndpoints.FIAT_ACCOUNTS}/${fiatAccountId}`, {
      params: { session_id: sessionId },
    });
  }

  public async deleteFiatAccount(
    sessionId: string,
    fiatAccountId: string,
  ): Promise<{ deleted?: boolean; message?: string }> {
    return this.request<{ deleted?: boolean; message?: string }>(
      "DELETE",
      `${AlfredEndpoints.FIAT_ACCOUNTS}/${fiatAccountId}`,
      { params: { session_id: sessionId } },
    );
  }

  /**
   * Stateless metadata — per-rail required-fields schema. Cache on the
   * widget side (no auth, mostly static).
   */
  public async getFiatAccountRequirements(): Promise<AlfredFiatAccountRequirements> {
    return this.request<AlfredFiatAccountRequirements>("GET", AlfredEndpoints.FIAT_ACCOUNT_REQUIREMENTS);
  }
}

export const alfredService = new AlfredClient(getAlfredApiUrl());
